import { HistorySource } from '../../../components/HistorySource';
import { ZipaCashForm } from './ZipaCashForm';
import { resolveCashMode } from '../../accounting/cash/cashRecovery';
import type { CashCurrentContextV1 } from '@pokerroom/types';
import { ZipaJournalPanel } from './ZipaJournalPanel';
import { ZipaTransferPanel } from './ZipaTransferPanel';
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { MemberZipaHistoryDto } from '@pokerroom/types';
import { api } from '../../../lib/api';
import { formatAccountingInstant } from '../../../lib/accounting-time';
import { useAccountingDisplayTimezone } from '../../../hooks/useAccountingDisplayTimezone';
import { sessionVenueKey, useAuthStore } from '../../../store/auth.store';
import { LedgerHistoryPager } from './LedgerHistoryPager';
import { formatPoints } from './ledgerTabs';
import { hasPendingCashIntent } from './zipaTabState';

const PAGE_SIZE = 20;

/**
 * One authoritative cash operation surface and history; transfer and journal remain below.
 */
export function ZipaTab({ memberId }: { memberId: string }) {
  const session = useAuthStore((s) => s.sessionRevision);
  const actor = useAuthStore((s) => s.user?.id);
  const venue = useAuthStore((s) => sessionVenueKey(s.accessToken));
  const permissions = useAuthStore((s) => JSON.stringify(s.allowedActions));
  const scope = `${memberId}:${actor ?? ''}:${session}:${venue}:${permissions}`;
  return <ZipaTabContent key={scope} memberId={memberId} scope={scope} />;
}

function ZipaTabContent({ memberId, scope }: { memberId: string; scope: string }) {
  const { t, i18n } = useTranslation('member');
  const displayTimezone = useAccountingDisplayTimezone();
  const qc = useQueryClient();
  const venueId = useAuthStore((s) => sessionVenueKey(s.accessToken));
  const canReadCash = useAuthStore((s) => !!s.allowedActions?.includes('accounting:read'));
  const cashDay = useQuery({
    queryKey: ['cash-context', scope],
    enabled: canReadCash,
    retry: false,
    queryFn: () => api.get<{ cashContext: CashCurrentContextV1 }>('/accounting/business-day'),
  });
  const cashMode = resolveCashMode(cashDay.data?.cashContext, venueId);
  const [page, setPage] = useState(1);
  // Keep unfinished financial requests visible when the scoped surface is restored.
  const [transferOpen] = useState(() => {
    const state = useAuthStore.getState();
    try {
      return !!sessionStorage.getItem(
        `member-financial:${state.user?.id}:${sessionVenueKey(state.accessToken)}:${memberId}:zipa-transfers`,
      );
    } catch {
      return false;
    }
  });
  const [cashOpen] = useState(() => {
    try {
      return hasPendingCashIntent(memberId, window.sessionStorage);
    } catch {
      return false;
    }
  });

  const { data, isError } = useQuery<MemberZipaHistoryDto>({
    queryKey: ['member-zipa', memberId, scope, page],
    queryFn: () => api.get(`/members/${memberId}/zipa`, { params: { page, limit: PAGE_SIZE } }),
  });

  return (
    <div className="space-y-4">
      {cashMode === 'LOCATION_V1' && cashDay.data ? (
        <ZipaCashForm
          memberId={memberId}
          context={cashDay.data.cashContext}
          refresh={async () => {
            await Promise.all(
              [
                'member-zipa',
                'zipa-journal',
                'member-ledger-summary',
                'member',
                'zipa-daily-totals',
                'member-zipa-balances',
                'daily-ledgers',
              ].map((key) =>
                qc.invalidateQueries(
                  {
                    queryKey: [
                      'daily-ledgers',
                      'zipa-daily-totals',
                      'member-zipa-balances',
                    ].includes(key)
                      ? [key]
                      : [key, memberId],
                  },
                  { throwOnError: true },
                ),
              ),
            );
          }}
        />
      ) : (
        <p role="status">
          {cashMode === 'UNKNOWN' ? t('accounting:cash.unknown') : t('zipaTab.cashUnavailable')}
        </p>
      )}

      <div className="bg-base-200 border border-base-300 rounded-lg overflow-x-auto">
        <table className="w-full min-w-[36rem] text-sm">
          <thead className="bg-base-300">
            <tr>
              <th className="text-left px-4 py-2">{t('ledgerType')}</th>
              <th className="text-left px-4 py-2">{t('ledgerMoneyDelta')}</th>
              <th className="text-left px-4 py-2">{t('ledgerNote')}</th>
              <th className="text-left px-4 py-2">{t('ledgerTime')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-base-300">
            {data?.rows.map((row) => (
              <tr key={row.id}>
                <td className="px-4 py-2">
                  {row.cashEffect === 'CASH_REDEMPTION'
                    ? t('zipaTab.redemption')
                    : row.sourceKind === 'MIGRATION_BRIDGE'
                      ? t('ledgerZipaMigrationBridge')
                      : t(`ledgerZipaType_${row.type}`)}
                  {row.historical && row.sourceKind !== 'MIGRATION_BRIDGE' && (
                    <span className="block text-xs text-base-content/60">
                      {t('ledgerZipaMigratedHistory')}
                    </span>
                  )}
                </td>
                <td
                  className={`px-4 py-2 font-mono font-medium ${Number(row.delta) < 0 ? 'text-error' : 'text-success'}`}
                >
                  {Number(row.delta) > 0 ? '+' : ''}
                  {formatPoints(row.delta)}
                </td>
                <td className="px-4 py-2 [overflow-wrap:anywhere]">
                  <HistorySource source={row.displaySource} note={row.note} />
                </td>
                <td className="px-4 py-2 text-base-content/60 text-xs whitespace-nowrap">
                  {formatAccountingInstant(row.createdAt, displayTimezone ?? '', i18n.language) ??
                    t('accounting:dailyLedgers.unavailable')}
                </td>
              </tr>
            ))}
            {data && data.rows.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-base-content/50">
                  {t('ledgerEmpty')}
                </td>
              </tr>
            )}
            {isError && (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-error">
                  {t('ledgerLoadFailed')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {data && (
          <LedgerHistoryPager
            page={data.page}
            limit={data.limit}
            total={data.total}
            onPage={setPage}
          />
        )}
      </div>
      <details className="rounded border border-base-300 p-4" open={cashOpen || transferOpen}>
        <summary className="min-h-11 cursor-pointer font-semibold">
          {t('zipaTab.otherCash')}
        </summary>
        <div className="mt-3 space-y-4">
          <ZipaTransferPanel memberId={memberId} />
        </div>
      </details>
      <details className="rounded border border-base-300 p-4">
        <summary className="min-h-11 cursor-pointer font-semibold">
          {t('zipaTab.technical')}
        </summary>
        <div className="mt-3">
          <ZipaJournalPanel memberId={memberId} />
        </div>
      </details>
    </div>
  );
}
